import { Box, Center, Link, List, ListItem, SimpleGrid, Spinner, Text } from '@chakra-ui/react';
import { Contact } from 'models';
import { useRouter } from 'next/router';
import { Fragment } from 'react';
import { usePaginatedPeople } from 'store/paginated-people';
import { withTranslations } from 'store/translations';
import { peopleCall } from 'utils/peopleCall';

import { CountriesWithOfficesCollection } from './types';

type OfficesListProps = {
  countriesWithOffices: CountriesWithOfficesCollection;
};

const OfficesList = ({ countriesWithOffices }: OfficesListProps) => {
  const { multiple } = withTranslations();
  const router = useRouter();
  const { setNames } = usePaginatedPeople();
  const limit = 1000;

  if (!router.isReady) {
    // return null;
    return (
      <Center py={12}>
        <Spinner size='lg' />
      </Center>
    );
  }

  const getFilteredCountries = () => {
    // Check if there's a filter query parameter
    const filterParam = router.query.filter as string;
    const localeCountryFilters: Record<string, string[]> = {
      apac: [
        'australia',
        'bahrain',
        'china',
        'hong kong',
        'india',
        'japan',
        'malaysia',
        'new-zealand',
        'qatar',
        'saudi arabia',
        'singapore',
        'south korea',
        'united arab emirates',
      ],
      americas: ['united states', 'canada', 'mexico'],
    };

    // Only filter if there's a specific filter parameter
    if (filterParam && localeCountryFilters[filterParam]) {
      const allowedCountries = localeCountryFilters[filterParam];

      const filteredAll = countriesWithOffices.all.filter((country) =>
        allowedCountries.includes(country.title.toLowerCase()),
      );

      const filteredGrouped = countriesWithOffices.grouped
        .map(([region, countries]) => [
          region,
          countries.filter((country) => allowedCountries.includes(country.codename.toLowerCase())),
        ])
        .filter(([region, countries]) => countries.length > 0) as [string, typeof countriesWithOffices.all][];

      return {
        all: filteredAll,
        grouped: filteredGrouped,
      };
    }

    // Return original data when no filter is applied
    return countriesWithOffices;
  };

  const filteredCountriesWithOffices = getFilteredCountries();
  return (
    <>
      <SimpleGrid
        as='ul'
        columns={{ base: 1, md: 2, lg: 3 }}
        display={{ base: 'none', md: 'grid' }}
        listStyleType='none'
      >
        {filteredCountriesWithOffices.all.map(({ title, codename, url }) => (
          <Box key={codename} as='li' mb='m'>
            <Link
              as={'a'}
              color='mountain.base'
              href={`/${router.locale}/${multiple('contactUsUrl')}/${url}`}
              textStyle='desktop.h5'
            >
              {title}
            </Link>
          </Box>
        ))}
      </SimpleGrid>
      <List display={{ base: 'block', md: 'none' }}>
        {filteredCountriesWithOffices.grouped.map(([category, group]) => (
          <Fragment key={category}>
            <ListItem color='mountain.80' mb='s'>
              <Text textStyle='mobile.body.s'>{category}</Text>
            </ListItem>
            {group.map(({ title, codename, url }) => (
              <ListItem key={`${category}-${codename}`} mb='s'>
                <Link
                  as={'a'}
                  color='mountain.base'
                  href={`/${router.locale}/${multiple('contactUsUrl')}/${url}`}
                  textStyle={{ base: 'mobile.h4', lg: 'desktop.h5' }}
                >
                  {title}
                </Link>
              </ListItem>
            ))}
          </Fragment>
        ))}
      </List>
    </>
  );
};

export default OfficesList;
